/* global G */
import redirectSequence from 'lib/sequence/module/adapter/router/redirect'
import validationResult from 'app/support/action/validateRedirect'
import { asyncPipeSpread, deleteKey, setKey } from 'lib/util'
import find from 'lib/sequence/component/children/find'

/**
 * Attempts to send a "password reset" request according to the current model values.
 *
 * @param {Gaia.AppModule.Spec} module  the current module composition object
 * @returns {Promise<void>} a pending promise object waiting for the request's result
 * @private
 */
const _submit = async (module) => {
  const model = module[G.MODEL]
  const httpAdapter = model[G.ADAPTER][G.HTTP]

  // const token = module[G.ADAPTER][G.SESSION][G.STATE][G.REF]
  const { version, api } = model[G.PROPS]
  const url = `/api/v${version}/${api}/password/reset`

  const params = {
    token: module[G.ADAPTER][G.SESSION][G.STATE][G.REF],
    password: model[G.CHILDREN].change[G.CHILDREN].password[G.DATA].value,
  }

  try {
    await httpAdapter.post({ url, params }, {
      middleware: ({ error, invite, ...middleware }) => Object.values(middleware),
    })
    const moduleState = module[G.STATE]
    moduleState[G.ROUTE] = {
      [G.ACTION]: 'changeSuccess',
    }
  } catch {
    const moduleState = module[G.STATE]
    moduleState[G.ROUTE] = {
      [G.ACTION]: 'changeError',
    }
  } finally {
    deleteKey(G.UNDO, module[G.STATE][G.ACTION][G.STATE])
    await redirectSequence(module)({})
  }
}

/**
 * Validates the password reset form.
 *
 * @param {Gaia.AppModule.Spec} module the current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const validate = module => async (components, ...args) => {
  const result = validationResult(module)()(...args)

  return [components, ...result, ...args]
}

/**
 * Displays potential validation errors
 *
 * @param {Gaia.AppModule.Spec} module the current module composition
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const displayValidationErrors = module => async (components, ...args) => {
  const model = module[G.MODEL]

  if (model[G.STATE][G.ERROR]) {
    const { minLength, specialCharacter } = components

    const { change } = model[G.CHILDREN]
    const { requirements = {} } = change[G.STATE]?.[G.ERROR] || {}

    requirements.minLength || requirements.notEmpty
      ? setKey('error', 'color', minLength[G.STATE])
      : deleteKey('color', minLength[G.STATE])

    requirements.specialCharacter
      ? setKey('error', 'color', specialCharacter[G.STATE])
      : deleteKey('color', specialCharacter[G.STATE])
  }

  return args
}

/**
 *
 * @param module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const resetPassword = module => async (components, ...args) => {
  const model = module[G.MODEL]

  if (!model[G.STATE][G.ERROR]) {
    await _submit(module)
  }

  return args
}

/**
 * Password Reset Action
 *
 * @param {Gaia.AppModule.Spec} module the current module composition
 * @returns {function(*): function(...[*]): Promise<*>}
 */
export default module => component => async (...args) => asyncPipeSpread(
  validate(module),
  displayValidationErrors(module),
  resetPassword(module),
)(find(module[G.STATE][G.ACTION][G.COMPONENT]), ...args)
